import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Button,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState, useEffect } from "react";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import { updateUserPassword } from "../../services/UserService";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";

import AppDialog from "../General/Dialog/AppDialog";
import { useToast } from "../../context/ToastContext";
// import CardHeader from "../General/CardHeader";

export default function PasswordModal({
  open,
  onClose,
  userId,
  onSuccess,
}) {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    password: "",
    password_confirmation: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        password: "",
        password_confirmation: "",
      });
      setErrors({});
      setShowPassword1(false);
      setShowPassword2(false);
    }
  }, [open]);

  const validatePasswordRules = (password) => {
    const rules = {
      minLength: password.length >= 8,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
    };

    return rules;
  };

  const getPasswordError = (password) => {
    if (!password) {
      return "Contraseña requerida";
    }

    const rules = validatePasswordRules(password);

    if (!rules.minLength) {
      return "Debe tener al menos 8 caracteres";
    }

    if (!rules.hasLowercase) {
      return "Debe contener al menos una letra minúscula";
    }

    if (!rules.hasUppercase) {
      return "Debe contener al menos una letra mayúscula";
    }

    if (!rules.hasNumber) {
      return "Debe contener al menos un número";
    }

    return "";
  };

  const validate = () => {
    const temp = {};

    const passwordError = getPasswordError(form.password);
    if (passwordError) {
      temp.password = passwordError;
    }

    if (!form.password_confirmation) {
      temp.password_confirmation = "Debe confirmar la contraseña";
    } else if (form.password !== form.password_confirmation) {
      temp.password_confirmation = "Las contraseñas no coinciden";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]:
        name === "password"
          ? ""
          : name === "password_confirmation"
            ? ""
            : prev[name],
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        password: form.password,
        password_confirmation: form.password_confirmation,
      };

      const response = await updateUserPassword(userId, payload);

      if (response.data?.status === "success") {
        onSuccess(
          response.data.message || "Contraseña actualizada correctamente.",
        );
        onClose();
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        const apiErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          apiErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(apiErrors);
      } else if (error?.response?.data?.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("Error al actualizar la contraseña.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const passwordRules = validatePasswordRules(form.password);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Actualizar Contraseña"
      description="Ingrese su nueva contraseña y confírmela"
      icon={<LockOpenOutlinedIcon />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            endIcon={<CancelIcon />}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            endIcon={<SendIcon />}
            disabled={loading}
          >
            Actualizar
          </Button>
        </>
      }
    >
      {/* <CardHeader
        icon={<LockOpenOutlinedIcon />}
        title="Actualizar Contraseña"
        description=""
      /> */}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{ mb: 2 }}
          error={!!errors.password}
        >
          <InputLabel>Nueva Contraseña</InputLabel>
          <OutlinedInput
            type={showPassword1 ? "text" : "password"}
            name="password"
            value={form.password}
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword1(!showPassword1)}
                  edge="end"
                >
                  {showPassword1 ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Nueva Contraseña"
          />
          <FormHelperText>{errors.password}</FormHelperText>
        </FormControl>

        <Box
          sx={{
            mb: 2,
            p: 1.6,
            borderRadius: 3,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.58)",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.2,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.72)",
                border: "1px solid rgba(25,118,210,0.18)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <SecurityRoundedIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 900, fontSize: 14.5, lineHeight: 1.15 }}
              >
                Requisitos de seguridad
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.68, mt: 0.2 }}>
                La contraseña debe cumplir estas condiciones.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gap: 0.8 }}>
            {[
              {
                ok: passwordRules.minLength,
                label: "Al menos 8 caracteres",
              },
              {
                ok: passwordRules.hasLowercase,
                label: "Una letra minúscula",
              },
              {
                ok: passwordRules.hasUppercase,
                label: "Una letra mayúscula",
              },
              {
                ok: passwordRules.hasNumber,
                label: "Un número",
              },
            ].map((rule, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.1,
                  py: 0.9,
                  borderRadius: 2.2,
                  border: "1px solid",
                  borderColor: rule.ok
                    ? "rgba(46,125,50,0.18)"
                    : isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
                  background: rule.ok
                    ? "rgba(46,125,50,0.08)"
                    : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
                  transition: "all .18s ease",
                }}
              >
                {rule.ok ? (
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: "success.main",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <RadioButtonUncheckedRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: "text.disabled",
                      flexShrink: 0,
                    }}
                  />
                )}

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: rule.ok ? 800 : 600,
                    color: rule.ok ? "success.main" : "text.primary",
                    opacity: rule.ok ? 1 : 0.78,
                    lineHeight: 1.2,
                  }}
                >
                  {rule.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <FormControl
          fullWidth
          variant="outlined"
          error={!!errors.password_confirmation}
        >
          <InputLabel>Confirmar Contraseña</InputLabel>
          <OutlinedInput
            type={showPassword2 ? "text" : "password"}
            name="password_confirmation"
            value={form.password_confirmation}
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword2(!showPassword2)}
                  edge="end"
                >
                  {showPassword2 ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Confirmar Contraseña"
          />
          <FormHelperText>{errors.password_confirmation}</FormHelperText>
        </FormControl>
      </Box>
    </AppDialog>
  );
}
